/**
 * Pincode Checker — Delivery & COD availability
 * Uses India Post API for city/state lookup.
 * Isolated IIFE, no framework dependencies.
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Config — add pincodes to these arrays to mark them unavailable
  // ---------------------------------------------------------------------------
  var COD_UNAVAILABLE_PINCODES = [];
  var SHIPPING_UNAVAILABLE_PINCODES = [];

  var PINCODE_REGEX = /^[1-9][0-9]{5}$/;
  var API_URL = 'https://api.postalpincode.in/pincode/';

  // ---------------------------------------------------------------------------
  // Date helpers
  // ---------------------------------------------------------------------------
  function ordSuffix(d) {
    if (d >= 11 && d <= 13) return 'th';
    switch (d % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }

  function fmtDate(date) {
    var m = date.toLocaleDateString('en-US', { month: 'short' });
    var d = date.getDate();
    return m + ' ' + d + ordSuffix(d);
  }

  function getDeliveryDates() {
    var today = new Date();

    var readyStart = new Date(today);
    readyStart.setDate(today.getDate() + 1);

    var readyEnd = new Date(today);
    readyEnd.setDate(today.getDate() + 2);

    var delivStart = new Date(today);
    delivStart.setDate(today.getDate() + 4);

    var delivEnd = new Date(today);
    delivEnd.setDate(today.getDate() + 7);

    return {
      ordered: fmtDate(today),
      ready: fmtDate(readyStart) + ' \u2013 ' + fmtDate(readyEnd),
      deliveredRange: fmtDate(delivStart) + ' \u2013 ' + fmtDate(delivEnd)
    };
  }

  // ---------------------------------------------------------------------------
  // SVG icons for timeline
  // ---------------------------------------------------------------------------
  var ICON_ORDERED =
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<rect x="5" y="2" width="14" height="20" rx="2" stroke="currentColor" stroke-width="1.5"/>' +
    '<path d="M9 7h6M9 11h6M9 15h4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
    '</svg>';

  var ICON_SHIP =
    '<svg width="22" height="18" viewBox="0 0 22 18" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M1 1h11v10H1V1z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
    '<path d="M12 5h4.5L19 8.5V11h-7V5z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
    '<circle cx="5" cy="15" r="2" stroke="currentColor" stroke-width="1.5"/>' +
    '<circle cx="16" cy="15" r="2" stroke="currentColor" stroke-width="1.5"/>' +
    '</svg>';

  var ICON_BOX =
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M21 16V8a2 2 0 0 0-1-1.73L13 2.27a2 2 0 0 0-2 0L4 6.27A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
    '<path d="M3.27 6.96 12 12.01 20.73 6.96M12 22.08V12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
    '</svg>';

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------
  function renderLoading(resultEl) {
    resultEl.innerHTML = '<span class="lvd-pc__loading">Checking\u2026</span>';
  }

  function renderError(resultEl, msg) {
    resultEl.innerHTML = '<span class="lvd-pc__city lvd-pc__city--error">' + msg + '</span>';
  }

  function renderUnavailable(resultEl, pincode) {
    resultEl.innerHTML =
      '<span class="lvd-pc__city lvd-pc__city--no">' +
        '<svg class="lvd-pc__city-icon" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">' +
          '<circle cx="7" cy="7" r="6.5" stroke="currentColor"/>' +
          '<path d="M4.5 4.5l5 5M9.5 4.5l-5 5" stroke="currentColor" stroke-linecap="round"/>' +
        '</svg>' +
        'Delivery not available to ' + pincode +
      '</span>';
  }

  function renderSuccess(resultEl, city, state, codAvail, sepEl, codEl) {
    var dates = getDeliveryDates();

    // City result row
    var cityHtml =
      '<div class="lvd-pc__city lvd-pc__city--yes">' +
        '<svg class="lvd-pc__city-icon" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">' +
          '<circle cx="7" cy="7" r="6.5" stroke="currentColor"/>' +
          '<path d="M3.5 7l2.5 2.5L10.5 4" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>' +
        'Deliverable to ' + city.toUpperCase() + ' (' + state.toUpperCase() + ')' +
      '</div>';

    // Timeline
    var tlHtml =
      '<div class="lvd-pc__timeline">' +
        '<div class="lvd-pc__tl-line"></div>' +
        '<div class="lvd-pc__tl-step">' +
          '<div class="lvd-pc__tl-node lvd-pc__tl-node--done">' + ICON_ORDERED + '</div>' +
          '<div class="lvd-pc__tl-label">Ordered</div>' +
          '<div class="lvd-pc__tl-date">' + dates.ordered + '</div>' +
        '</div>' +
        '<div class="lvd-pc__tl-step">' +
          '<div class="lvd-pc__tl-node lvd-pc__tl-node--done">' + ICON_SHIP + '</div>' +
          '<div class="lvd-pc__tl-label">Ready to Ship</div>' +
          '<div class="lvd-pc__tl-date">' + dates.ready + '</div>' +
        '</div>' +
        '<div class="lvd-pc__tl-step">' +
          '<div class="lvd-pc__tl-node lvd-pc__tl-node--active">' + ICON_BOX + '</div>' +
          '<div class="lvd-pc__tl-label lvd-pc__tl-label--active">Delivered</div>' +
          '<div class="lvd-pc__tl-date lvd-pc__tl-date--active">' + dates.deliveredRange + '</div>' +
        '</div>' +
      '</div>';

    resultEl.innerHTML = cityHtml + tlHtml;

    // Show separator before COD badge
    if (sepEl) sepEl.style.display = 'block';

    // Update COD badge
    if (codEl) {
      if (codAvail) {
        codEl.className = 'lvd-pc__cod lvd-pc__cod--available';
        codEl.querySelector('span').textContent = 'COD Available';
      } else {
        codEl.className = 'lvd-pc__cod lvd-pc__cod--unavailable';
        codEl.querySelector('span').textContent = 'COD Not Available';
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Core: fetch pincode info from India Post API
  // ---------------------------------------------------------------------------
  function fetchPincodeData(pincode, callback) {
    var xhr = new XMLHttpRequest();
    xhr.open('GET', API_URL + pincode, true);
    xhr.timeout = 5000;
    xhr.onload = function () {
      if (xhr.status === 200) {
        try {
          var data = JSON.parse(xhr.responseText);
          if (
            data &&
            data[0] &&
            data[0].Status === 'Success' &&
            data[0].PostOffice &&
            data[0].PostOffice.length > 0
          ) {
            var po = data[0].PostOffice[0];
            callback(null, {
              city: po.District || po.Division || po.Name,
              state: po.State
            });
          } else {
            callback('not_found');
          }
        } catch (e) {
          callback('parse_error');
        }
      } else {
        callback('network_error');
      }
    };
    xhr.onerror = function () { callback('network_error'); };
    xhr.ontimeout = function () { callback('timeout'); };
    xhr.send();
  }

  // ---------------------------------------------------------------------------
  // Init
  // ---------------------------------------------------------------------------
  function init() {
    var widgets = document.querySelectorAll('.lvd-pc');
    if (!widgets.length) return;

    widgets.forEach(function (widget) {
      var input    = widget.querySelector('.lvd-pc__input');
      var btn      = widget.querySelector('.lvd-pc__btn');
      var resultEl = widget.querySelector('.lvd-pc__result');
      var sepEl    = widget.querySelector('.lvd-pc__separator');
      var codEl    = widget.querySelector('.lvd-pc__cod');

      if (!input || !btn || !resultEl) return;

      function handleCheck() {
        var pincode = input.value.trim();

        if (!PINCODE_REGEX.test(pincode)) {
          resultEl.innerHTML = '';
          if (sepEl) sepEl.style.display = 'none';
          renderError(resultEl, 'Please enter a valid 6-digit pincode.');
          return;
        }

        // Check manual unavailable lists first (no API needed)
        if (SHIPPING_UNAVAILABLE_PINCODES.indexOf(pincode) !== -1) {
          if (sepEl) sepEl.style.display = 'none';
          renderUnavailable(resultEl, pincode);
          return;
        }

        var codAvail = COD_UNAVAILABLE_PINCODES.indexOf(pincode) === -1;

        btn.disabled = true;
        btn.textContent = '\u2026';
        renderLoading(resultEl);

        fetchPincodeData(pincode, function (err, info) {
          btn.disabled = false;
          btn.textContent = 'Check';

          if (err === 'not_found') {
            if (sepEl) sepEl.style.display = 'none';
            renderUnavailable(resultEl, pincode);
            return;
          }

          if (err) {
            // Fallback: show generic success without city name
            info = { city: 'your location', state: '' };
          }

          renderSuccess(resultEl, info.city, info.state, codAvail, sepEl, codEl);

          // Persist
          try {
            sessionStorage.setItem('lvd_last_pincode', pincode);
          } catch (e) { /* ignore */ }
        });
      }

      btn.addEventListener('click', handleCheck);

      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') handleCheck();
      });

      input.addEventListener('input', function () {
        input.value = input.value.replace(/\D/g, '');
        if (!input.value) {
          resultEl.innerHTML = '';
          if (sepEl) sepEl.style.display = 'none';
          // Reset COD to default
          if (codEl) {
            codEl.className = 'lvd-pc__cod';
            codEl.querySelector('span').textContent = 'COD Available';
          }
        }
      });

      // Restore last pincode
      try {
        var saved = sessionStorage.getItem('lvd_last_pincode');
        if (saved && PINCODE_REGEX.test(saved)) {
          input.value = saved;
          // Re-run silently
          var codAvail = COD_UNAVAILABLE_PINCODES.indexOf(saved) === -1;
          if (SHIPPING_UNAVAILABLE_PINCODES.indexOf(saved) !== -1) {
            renderUnavailable(resultEl, saved);
          } else {
            fetchPincodeData(saved, function (err, info) {
              if (err === 'not_found') { renderUnavailable(resultEl, saved); return; }
              if (err) { info = { city: 'your location', state: '' }; }
              renderSuccess(resultEl, info.city, info.state, codAvail, sepEl, codEl);
            });
          }
        }
      } catch (e) { /* ignore */ }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
